import React from 'react';
import { hash } from 'components/timeline_control/helpers';
import moment from 'moment';
import renderIf from 'render-if';
import Ticks from '../ticks';
import cx from 'classnames';

const BAR_WIDTH = 120;
const DIAMOND_WIDTH = 30;

/**
 * @typedef MilestoneRowProps
 * @prop {*=} scale
 * @prop {*=} timelineConfig
 * @prop {*=} width
 * @prop {*=} showMilestoneBars
 * @prop {*=} milestoneBarHeight
 * @prop {*=} updateMilestoneBarIndex
 */
/**
 * @type {React.FC<MilestoneRowProps>}
 */
const MilestoneRow = ({
  scale,
  timelineConfig,
  width,
  showMilestoneBars,
  milestoneBarHeight,
  updateMilestoneBarIndex,
}) => {
  const { milestones, milestonesShowTitles, milestonesShowDates } =
    timelineConfig;

  const key = `MilestoneRow-`;
  const milestoneMarkers = milestones.map((milestone, index) => {
    const milestoneMarkerKey = `MilestoneMarker-${hash(
      `${milestone.title}${index}`
    )}`;
    const momentDate = moment.utc(milestone.date);

    const dateOffset = scale.dateToRawOffset(momentDate);

    return (
      <div key={milestoneMarkerKey}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='31'
          height='31'
          viewBox='0 0 31 31'
          fill='none'
          style={{
            left: dateOffset - DIAMOND_WIDTH / 2,
            position: 'absolute',
            top: 4,
            zIndex: 2,
            cursor: 'pointer',
          }}
        >
          <g filter='url(#filter0_d_89_45320)'>
            <g filter='url(#filter1_d_89_45320)'>
              <path
                d='M14.7929 8.70711C15.1834 8.31658 15.8166 8.31658 16.2071 8.70711L22.2929 14.7929C22.6834 15.1834 22.6834 15.8166 22.2929 16.2071L16.2071 22.2929C15.8166 22.6834 15.1834 22.6834 14.7929 22.2929L8.70711 16.2071C8.31658 15.8166 8.31658 15.1834 8.70711 14.7929L14.7929 8.70711Z'
                fill='white'
              />
            </g>
            <path
              d='M14.7929 11.7071C15.1834 11.3166 15.8166 11.3166 16.2071 11.7071L19.2929 14.7929C19.6834 15.1834 19.6834 15.8166 19.2929 16.2071L16.2071 19.2929C15.8166 19.6834 15.1834 19.6834 14.7929 19.2929L11.7071 16.2071C11.3166 15.8166 11.3166 15.1834 11.7071 14.7929L14.7929 11.7071Z'
              fill={milestone.color}
            />
          </g>
          <defs>
            <filter
              id='filter0_d_89_45320'
              x='0.414185'
              y='0.414215'
              width='30.1716'
              height='30.1716'
              filterUnits='userSpaceOnUse'
              color-interpolation-filters='sRGB'
            >
              <feFlood flood-opacity='0' result='BackgroundImageFix' />
              <feColorMatrix
                in='SourceAlpha'
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                result='hardAlpha'
              />
              <feOffset />
              <feGaussianBlur stdDeviation='4' />
              <feColorMatrix
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0'
              />
              <feBlend
                mode='normal'
                in2='BackgroundImageFix'
                result='effect1_dropShadow_89_45320'
              />
              <feBlend
                mode='normal'
                in='SourceGraphic'
                in2='effect1_dropShadow_89_45320'
                result='shape'
              />
            </filter>
            <filter
              id='filter1_d_89_45320'
              x='0.414185'
              y='0.414215'
              width='30.1716'
              height='30.1716'
              filterUnits='userSpaceOnUse'
              color-interpolation-filters='sRGB'
            >
              <feFlood flood-opacity='0' result='BackgroundImageFix' />
              <feColorMatrix
                in='SourceAlpha'
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                result='hardAlpha'
              />
              <feOffset />
              <feGaussianBlur stdDeviation='4' />
              <feColorMatrix
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0'
              />
              <feBlend
                mode='normal'
                in2='BackgroundImageFix'
                result='effect1_dropShadow_89_45320'
              />
              <feBlend
                mode='normal'
                in='SourceGraphic'
                in2='effect1_dropShadow_89_45320'
                result='shape'
              />
            </filter>
          </defs>
        </svg>
        <aha-tooltip>{milestone.title}</aha-tooltip>
      </div>
    );
  });
  const milestoneBars = milestones.map((milestone, index) => {
    const milestoneBarKey = `MilestoneBar-${hash(
      `${milestone.title}${index}`
    )}`;
    const momentDate = moment.utc(milestone.date);

    const dateOffset = scale.dateToRawOffset(momentDate);

    let width = BAR_WIDTH;

    // Check if the milestone bar is off left or right of screen
    const isMilestoneBarOffScreen =
      dateOffset + BAR_WIDTH / 2 - scale.dateToRawOffset(scale.toDate) > 0 ||
      dateOffset - BAR_WIDTH / 2 <= scale.dateToRawOffset(scale.fromDate);

    let left = dateOffset - BAR_WIDTH / 2;

    if (isMilestoneBarOffScreen && dateOffset >= 0) {
      // If the bar is further left than the left edge of the screen, set its width to be shorter
      // and it's left position to be 1
      if (dateOffset <= BAR_WIDTH / 2) {
        width = BAR_WIDTH / 2 + dateOffset;
        left = 1;
      }
      // If the bar is further right than the right edge, set the width to be less than the full width of the bar
      else {
        width = BAR_WIDTH - (dateOffset + BAR_WIDTH / 2 - scale.width);
      }
    }

    return (
      <div key={milestoneBarKey}>
        {renderIf(milestonesShowTitles || milestonesShowDates)(
          <div
            className={cx('milestone-bar', {
              'milestone-bar-full': milestonesShowTitles && milestonesShowDates,
            })}
            style={{
              left: left,
              width: width,
              maxHeight: milestoneBarHeight,
              border: `1px solid ${milestone.color}`,
              padding: '4px 8px',
            }}
            onClick={e => updateMilestoneBarIndex(index)}
          >
            <div className='milestone-date'>
              {milestonesShowDates ? momentDate.format('MMMM D') : ''}
            </div>
            <div>{milestonesShowTitles ? milestone.title : ''}</div>
          </div>
        )}
        <aha-tooltip>{milestone.title}</aha-tooltip>
      </div>
    );
  });

  return (
    <div
      key={key}
      className='timeline-milestone-row'
      style={{
        width: width,
        backgroundColor: 'transparent',
      }}
    >
      <div>{milestoneMarkers}</div>
      <div className='milestone-bar-wrapper'>{milestoneBars}</div>
      <div>
        <Ticks
          scale={scale}
          width={scale.totalPixelWidth()}
          topOffset={20}
          showMilestoneBars={showMilestoneBars}
          milestones={timelineConfig.milestonesShowLines ? milestones : []}
        />
      </div>
    </div>
  );
};

export default MilestoneRow;
